import * as React from 'react';
import { FC, useMemo } from 'react';

import { StyleSheet } from 'react-native';

import { Box, Typography } from 'components/ui';
import { formatTime, t } from 'state/intl/translations';
import { usePickupTime } from 'state/order/hooks/use-pickup-time';
import { spacing } from 'styles/constants';
import { getLocalizedServiceMode } from 'utils/service-mode';

import { MinimalAccountOrder } from '../types';

const MinimalReceiptHeadInfo: FC<React.PropsWithChildren<{ order: MinimalAccountOrder }>> = ({
  order,
}) => {
  const message = t('orderDetailsHeading');
  const { pickupDate } = usePickupTime(order);
  const { formattedDate, formattedTime } = useMemo(() => {
    const orderDate = new Date(order.createdAt);
    const formattedOrderDate = orderDate.toLocaleString(t('localeDateTime'), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const formattedOrderTime = orderDate.toLocaleString('en-us', {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    });

    return { formattedDate: formattedOrderDate, formattedTime: formattedOrderTime };
  }, [order.createdAt]);

  const { storeAddress, serviceMode } = order.cart;
  const localizedServiceMode = getLocalizedServiceMode(serviceMode);
  const orderNumberLabel = t('orderNumber');
  const orderTimeLabel = t('orderTime');
  const serviceModeLabel = t('method', {
    serviceMode: '',
  });
  const orderPickUpTimeLabel = t('pickUpTime');

  return (
    <>
      <Typography
        variant="headerFour"
        style={styles.title}
        focusable
        accessible
        accessibilityRole="header"
        accessibilityLabel={message}
      >
        {message}
      </Typography>
      <Box>
        <Box style={styles.receiptInfo} accessible focusable>
          <Typography variant="copyTwo" style={[styles.receiptInfoLabel, styles.receiptDate]}>
            {formattedDate}
          </Typography>
        </Box>
        {order.orderNumberFormatted ? (
          <Box
            style={styles.receiptInfo}
            accessible
            focusable
            accessibilityLabel={`${orderNumberLabel}:${order.orderNumberFormatted}`}
          >
            <Typography variant="copyTwo" style={styles.receiptInfoLabel}>
              {orderNumberLabel}:
            </Typography>
            <Box style={styles.receiptInfoDetail}>
              <Typography variant="copyTwo">{order.orderNumberFormatted}</Typography>
            </Box>
          </Box>
        ) : null}
        <Box
          style={styles.receiptInfo}
          accessible
          focusable
          accessibilityLabel={`${orderTimeLabel}:${formattedTime}`}
        >
          <Typography variant="copyTwo" style={styles.receiptInfoLabel}>
            {orderTimeLabel}:
          </Typography>
          <Box style={styles.receiptInfoDetail}>
            <Typography variant="copyTwo">{formattedTime}</Typography>
          </Box>
        </Box>

        <Box
          style={styles.receiptInfo}
          accessible
          focusable
          accessibilityLabel={`${serviceModeLabel}:${localizedServiceMode}`}
        >
          <Typography variant="copyTwo" style={styles.receiptInfoLabel}>
            {serviceModeLabel}
          </Typography>
          <Box style={styles.receiptInfoDetail}>
            <Typography variant="copyTwo">{localizedServiceMode}</Typography>
          </Box>
        </Box>

        {!!order.fireOrderIn && (
          <Box
            style={styles.receiptInfo}
            accessible
            focusable
            accessibilityLabel={`${orderPickUpTimeLabel}:${formatTime(pickupDate)}`}
          >
            <Typography variant="copyTwo" style={styles.receiptInfoLabel}>
              {orderPickUpTimeLabel}:
            </Typography>
            <Box style={styles.receiptInfoDetail}>
              <Typography variant="copyTwo">{formatTime(pickupDate)}</Typography>
            </Box>
          </Box>
        )}

        <Box style={styles.receiptInfo} accessible focusable>
          <Typography variant="copyTwo" style={styles.receiptInfoLabel}>
            {t('location')}:
          </Typography>
          <Box style={styles.receiptInfoDetail}>
            <Typography variant="copyTwo" testID="receipt-address">
              {storeAddress.addressLine1}
              {', '}
              {storeAddress.addressLine2 &&
                `${t('unitNumber', { unitNumber: storeAddress.addressLine2 })}, `}
              {storeAddress.city}
              {', '}
              {storeAddress.state}
              {', '}
              {storeAddress.zip}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MinimalReceiptHeadInfo;

const styles = StyleSheet.create({
  receiptDate: {
    marginVertical: spacing.$2,
  },
  receiptInfo: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  receiptInfoDetail: {
    flexShrink: 1,
  },
  receiptInfoLabel: {
    alignSelf: 'flex-start',
    marginRight: spacing.$1,
  },
  title: {
    margin: 0,
  },
});
